import * as React from "react";
import {
  ProductLogo,
  Paragraph,
  Size,
  ScrollableDialog,
  HeaderArea,
  BorderlessSearchBar,
  InputSize,
  BottomBar,
  PrimaryButton,
  Formsy,
  Autofocus,
  BackButton,
  ButtonContainer,
  PageProgress,
  PanelPosition,
} from "@root/shared";
import { Provider, observer } from "mobx-react";
import { GitHubAppUIStore } from "./github-app-ui-store";
import { Finishing } from "./finishing";
import { RouteComponentProps, WithRouterProps } from "react-router";
import { noop, last } from "lodash";
import { withTranslation, WithTranslation } from "react-i18next";
import { RepositoriesErrorDialog, UserMatchErrorDialog, OrgPermissionErrorDialog, BillingPlanErrorDialog } from "./error-dialogs";
import * as PageTimer from "@root/shared/page-timer";
import { locationStore } from "@root/stores";
const styles = require("./github-app.scss");

export const GitHubAppShell = withTranslation("management")(
  // preload management namespace for add new app screen
  observer(
    class GitHubAppShell extends React.Component<
      { children: JSX.Element } & WithTranslation & WithRouterProps & RouteComponentProps<{ repoId: string }, {}>
    > {
      private githubAppUIStore = new GitHubAppUIStore(
        this.props.location.query.installation_id,
        this.props.location.query.access_token_id,
        this.props.location.query.github_user_id
      );
      private Form: React.SFC = (props) => (
        <Formsy.Form onValidSubmit={() => this.githubAppUIStore.createAppAndRedirect(this.props.params.repoId)} {...props} />
      );

      public UNSAFE_componentWillMount() {
        // Initialize the router.
        locationStore.setRouter(this.props.router);
      }

      private get copyright(): JSX.Element {
        return <div>© {new Date().getFullYear()} Microsoft</div>;
      }

      private get isAddNewAppRoute() {
        return last(this.props.routes).path === ":repoId";
      }

      private get header() {
        if (this.isAddNewAppRoute) {
          return (
            <>
              <HeaderArea
                title="Create app"
                subtitle="Creates a new app on App Center linked to the selected repository."
                smallSubtitle
              />
              <PageProgress panelPosition={PanelPosition.Primary} loading={this.githubAppUIStore.isLoading} />
            </>
          );
        }

        const { isLoading, data, query, setQuery } = this.githubAppUIStore;
        const results = isLoading ? 0 : data.length;
        return (
          <>
            <HeaderArea title="Select repository">
              <Autofocus focus={!isLoading}>
                <BorderlessSearchBar
                  disabled={isLoading}
                  data-test-id="searchbar"
                  size={InputSize.Small}
                  placeholder="Search repositories"
                  resultsCount={results}
                  onChange={setQuery}
                  value={query}
                />
              </Autofocus>
            </HeaderArea>
            <PageProgress panelPosition={PanelPosition.Primary} loading={this.githubAppUIStore.isLoading} />
          </>
        );
      }

      private get footer() {
        if (this.isAddNewAppRoute) {
          const { isLoading } = this.githubAppUIStore;
          return (
            <BottomBar alignRight>
              <ButtonContainer className={styles.footerButtons}>
                <BackButton to={`/github-app/setup/repositories${window.location.search}`} />
                <PrimaryButton type="submit" disabled={isLoading}>
                  Create
                </PrimaryButton>
              </ButtonContainer>
            </BottomBar>
          );
        }
      }

      public render() {
        const { children, route } = this.props;
        const {
          repositoriesErrorDialogVisible,
          userMatchErrorDialogVisible,
          orgPermissionErrorDialogVisible,
          billingPlanErrorDialogVisible,
          finishing,
        } = this.githubAppUIStore;
        const errorDialog = (() => {
          switch (true) {
            case repositoriesErrorDialogVisible:
              return <RepositoriesErrorDialog githubUsername={this.props.location.query.github_user_name} />;
            case userMatchErrorDialogVisible:
              return <UserMatchErrorDialog githubUsername={this.props.location.query.github_user_name} />;
            case orgPermissionErrorDialogVisible:
              return <OrgPermissionErrorDialog orgName={this.props.location.query.github_user_name} />;
            case billingPlanErrorDialogVisible:
              return <BillingPlanErrorDialog />;
          }
        })();
        return (
          <PageTimer.Collector>
            <div className={styles.background}>
              <ProductLogo invert />
              <Provider githubAppUIStore={this.githubAppUIStore}>
                {errorDialog ? (
                  errorDialog
                ) : finishing ? (
                  <Finishing githubAppUIStore={this.githubAppUIStore} />
                ) : (
                  <ScrollableDialog
                    fullHeight
                    data-test-id="github-app-dialog"
                    data-test-state={route.path}
                    blockTagName={this.Form}
                    visible={true}
                    onRequestClose={noop}
                    width={580}
                    maxHeight={729}
                    header={this.header}
                    bottomBar={this.footer}
                  >
                    {children}
                  </ScrollableDialog>
                )}
              </Provider>
              <Paragraph className={styles.copyright} invert size={Size.Small}>
                {this.copyright}
              </Paragraph>
            </div>
          </PageTimer.Collector>
        );
      }
    }
  )
);
